import React, { useState, useEffect, useRef } from 'react';
import { Star, Upload, Trash2, Image, Users, Settings, Flag, AlertCircle, CheckCircle, XCircle, Eye, Loader, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminService } from '../services/adminService';
import { reportService } from '../services/reportService';
import { Robot, User as UserType, HeroImage, HeroCardText, Report } from '../types';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RobotLogo from '../components/RobotLogo';
import { C } from '../design';
import toast from 'react-hot-toast';

const AdminPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('featured');

  // Featured robots state
  const [robots, setRobots] = useState<Robot[]>([]);
  const [robotsLoading, setRobotsLoading] = useState(true);

  // Hero images & card text state
  const [heroImages, setHeroImages] = useState<HeroImage[]>([]);
  const [heroCards, setHeroCards] = useState<HeroCardText[]>([
    { name: '', category: '', price: '', location: '', owner: '' },
    { name: '', category: '', price: '', location: '', owner: '' },
  ]);
  const [cardTextSaving, setCardTextSaving] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [uploadingSlot, setUploadingSlot] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeSlotRef = useRef<number>(0);

  // Users state
  const [users, setUsers] = useState<UserType[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Reports state
  const [reports, setReports] = useState<Report[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportFilter, setReportFilter] = useState<string>('all');

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState<{ totalViews: number; viewsByDay: { date: string; count: number }[]; viewsByPage: { page: string; count: number }[] } | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  // Cleanup state
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    if (activeTab === 'featured' && robotsLoading) fetchRobots();
    if (activeTab === 'images' && imagesLoading) fetchImages();
    if (activeTab === 'users' && usersLoading) fetchUsers();
    if (activeTab === 'reports' && reportsLoading) fetchReports();
    if (activeTab === 'analytics' && analyticsLoading) fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchRobots = async () => {
    try { setRobots(await adminService.getAllRobots()); }
    catch { toast.error('Failed to load robots'); }
    finally { setRobotsLoading(false); }
  };

  const fetchImages = async () => {
    try {
      const content = await adminService.getHomepageContent();
      setHeroImages(content.heroImages);
      if (content.heroCards?.length > 0) {
        // Ensure we always have 2 card entries
        const cards = [
          content.heroCards[0] || { name: '', category: '', price: '', location: '', owner: '' },
          content.heroCards[1] || { name: '', category: '', price: '', location: '', owner: '' },
        ];
        setHeroCards(cards);
      }
    } catch { toast.error('Failed to load images'); }
    finally { setImagesLoading(false); }
  };

  const fetchUsers = async () => {
    try { setUsers(await adminService.getAllUsers()); }
    catch { toast.error('Failed to load users'); }
    finally { setUsersLoading(false); }
  };

  const fetchReports = async () => {
    try { setReports(await reportService.getReports()); }
    catch { toast.error('Failed to load reports'); }
    finally { setReportsLoading(false); }
  };

  const fetchAnalytics = async (days?: number) => {
    setAnalyticsLoading(true);
    try { setAnalyticsData(await adminService.getAnalytics(days || analyticsDays)); }
    catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  const handleReportAction = async (reportId: string, action: 'reviewed' | 'resolved' | 'dismissed') => {
    try {
      await reportService.updateReportStatus(reportId, action);
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: action, updatedAt: new Date() } : r));
      toast.success(`Report ${action}`);
    } catch { toast.error('Failed to update report'); }
  };

  const handleCleanupOrphans = async () => {
    setCleaning(true);
    try {
      const result = await adminService.removeOrphanedRobots();
      if (result.removed === 0) {
        toast.success('No orphaned robots found');
      } else {
        toast.success(`Removed ${result.removed} orphaned robot${result.removed > 1 ? 's' : ''}: ${result.names.join(', ')}`);
        setRobots(prev => prev.filter(r => !result.names.includes(r.name)));
        fetchRobots(); // Refresh the full list
      }
    } catch { toast.error('Failed to clean up orphaned robots'); }
    finally { setCleaning(false); }
  };

  const handleToggleFeatured = async (robotId: string, current: boolean) => {
    try {
      await adminService.toggleFeatured(robotId, !current);
      setRobots(prev => prev.map(r => r.id === robotId ? { ...r, isFeatured: !current } : r));
      toast.success(current ? 'Removed from featured' : 'Added to featured');
    } catch { toast.error('Failed to update'); }
  };

  const handleSlotUpload = (slot: number) => {
    activeSlotRef.current = slot;
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }

    const slot = activeSlotRef.current;
    setUploadingSlot(slot);
    try {
      // Delete old image from storage if replacing
      if (heroImages[slot]?.url) {
        try { await adminService.deleteHeroImage(heroImages[slot].url); } catch {}
      }
      const url = await adminService.uploadHeroImage(file);
      const newImage: HeroImage = { url, alt: file.name.replace(/\.[^.]+$/, ''), order: slot };
      const updated = [...heroImages];
      updated[slot] = newImage;
      // Ensure array has no gaps
      while (updated.length < slot + 1) updated.push({ url: '', alt: '', order: updated.length });
      await adminService.updateHeroImages(updated.filter(img => img.url));
      setHeroImages(updated.filter(img => img.url));
      toast.success('Image uploaded');
    } catch (err: any) {
      console.error('Hero image upload error:', err);
      toast.error(`Upload failed: ${err?.message || 'Unknown error'}`);
    }
    finally { setUploadingSlot(null); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteImage = async (index: number) => {
    const image = heroImages[index];
    try {
      await adminService.deleteHeroImage(image.url);
      const updated = heroImages.filter((_, i) => i !== index).map((img, i) => ({ ...img, order: i }));
      await adminService.updateHeroImages(updated);
      setHeroImages(updated);
      toast.success('Image removed');
    } catch { toast.error('Failed to remove image'); }
  };

  const handleRoleChange = async (userId: string, currentRole: string | undefined) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (userId === currentUser?.uid) { toast.error("Can't change your own role"); return; }
    try {
      await adminService.setUserRole(userId, newRole as 'user' | 'admin');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole as 'user' | 'admin' } : u));
      toast.success(`User ${newRole === 'admin' ? 'promoted to admin' : 'set to regular user'}`);
    } catch { toast.error('Failed to update role'); }
  };

  const pendingCount = reports.filter(r => r.status === 'pending').length;
  const tabs = [
    { id: 'featured', name: 'Featured Robots', icon: <Star size={15} /> },
    { id: 'images', name: 'Homepage Images', icon: <Image size={15} /> },
    { id: 'users', name: 'Users', icon: <Users size={15} /> },
    { id: 'reports', name: 'Reports', icon: <Flag size={15} />, badge: pendingCount },
    { id: 'analytics', name: 'Analytics', icon: <BarChart3 size={15} /> },
  ];

  const cardStyle: React.CSSProperties = { background: C.pureWhite, border: `1px solid ${C.gray100}`, borderRadius: 12, padding: 24 };
  const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: C.gray400, padding: "10px 16px", textAlign: "left", borderBottom: `1px solid ${C.gray100}` };
  const tdStyle: React.CSSProperties = { padding: "14px 16px", borderBottom: `1px solid ${C.gray100}`, fontSize: 14 };

  const renderContent = () => {
    switch (activeTab) {
      case 'featured':
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em", marginBottom: 4 }}>Featured Robots</h3>
                <p style={{ fontSize: 14, color: C.gray400 }}>Toggle which robots appear in the featured section on the homepage.</p>
              </div>
              <button onClick={handleCleanupOrphans} disabled={cleaning}
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 100,
                  fontSize: 13, fontWeight: 500, background: "transparent", fontFamily: "inherit",
                  color: "#ef4444", border: `1px solid #ef4444`, cursor: cleaning ? "not-allowed" : "pointer",
                  opacity: cleaning ? 0.6 : 1, transition: "all 0.15s", flexShrink: 0,
                }}>
                {cleaning ? <Loader size={14} style={{ animation: "spin 0.8s linear infinite" }} /> : <Trash2 size={14} />}
                {cleaning ? 'Cleaning...' : 'Remove Orphaned'}
              </button>
            </div>
            {robotsLoading ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <div style={{ width: 32, height: 32, border: `3px solid ${C.gray200}`, borderTopColor: C.blue, borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : robots.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <RobotLogo color={C.gray300} size={36} />
                <p style={{ fontSize: 14, color: C.gray400, marginTop: 16 }}>No robots found.</p>
              </div>
            ) : (
              <div style={cardStyle}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Robot</th>
                      <th style={thStyle}>Category</th>
                      <th style={thStyle}>Price</th>
                      <th style={thStyle}>Rating</th>
                      <th style={thStyle}>Status</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Featured</th>
                    </tr>
                  </thead>
                  <tbody>
                    {robots.map(robot => (
                      <tr key={robot.id}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{robot.name}</div>
                          <div style={{ fontSize: 12, color: C.gray400 }}>{robot.location}</div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 12, fontWeight: 500, padding: "3px 10px", borderRadius: 100, border: `1px solid ${C.gray200}`, color: C.gray500 }}>{robot.category}</span>
                        </td>
                        <td style={tdStyle}>${robot.price}/day</td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Star size={13} fill={robot.rating > 0 ? "#f59e0b" : "none"} color={robot.rating > 0 ? "#f59e0b" : C.gray300} />
                            {robot.rating > 0 ? robot.rating.toFixed(1) : '—'}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: robot.isAvailable ? C.blue : C.gray400 }}>
                            {robot.isAvailable ? 'Available' : 'Rented'}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button
                            onClick={() => handleToggleFeatured(robot.id, !!robot.isFeatured)}
                            style={{
                              width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                              background: robot.isFeatured ? C.blue : C.gray200,
                              position: "relative", transition: "background 0.2s",
                            }}
                          >
                            <div style={{
                              width: 18, height: 18, borderRadius: "50%", background: C.pureWhite,
                              position: "absolute", top: 3,
                              left: robot.isFeatured ? 23 : 3,
                              transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                            }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'images': {
        const slots = [
          { label: 'Front Card Image', description: 'The main featured robot card in the hero section.', index: 0 },
          { label: 'Back Card Image', description: 'The secondary card shown behind the main card.', index: 1 },
        ];
        return (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em", marginBottom: 4 }}>Homepage Images</h3>
              <p style={{ fontSize: 14, color: C.gray400 }}>Manage the two hero images shown on the landing page.</p>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />

            {imagesLoading ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <div style={{ width: 32, height: 32, border: `3px solid ${C.gray200}`, borderTopColor: C.blue, borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {slots.map(slot => {
                  const image = heroImages[slot.index];
                  const isUploading = uploadingSlot === slot.index;
                  return (
                    <div key={slot.index} style={cardStyle}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{slot.label}</div>
                          <div style={{ fontSize: 13, color: C.gray400 }}>{slot.description}</div>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: C.gray400, padding: "3px 10px", borderRadius: 100, border: `1px solid ${C.gray200}` }}>
                          Slot {slot.index + 1}
                        </span>
                      </div>
                      {image ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                          <img src={image.url} alt={image.alt}
                            style={{ width: 200, height: 130, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.gray100}`, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{image.alt}</div>
                            <div style={{ fontSize: 12, color: C.gray400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 16 }}>{image.url}</div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={() => handleSlotUpload(slot.index)} disabled={isUploading}
                                style={{
                                  display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 100, fontSize: 13, fontWeight: 500,
                                  background: "transparent", color: C.blue, border: `1px solid ${C.blue}`, cursor: isUploading ? "not-allowed" : "pointer", fontFamily: "inherit",
                                }}>
                                <Upload size={14} /> {isUploading ? 'Uploading...' : 'Replace'}
                              </button>
                              <button onClick={() => handleDeleteImage(slot.index)}
                                style={{
                                  display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 100, fontSize: 13, fontWeight: 500,
                                  background: "transparent", color: "#ef4444", border: `1px solid ${C.gray200}`, cursor: "pointer", fontFamily: "inherit",
                                }}>
                                <Trash2 size={14} /> Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => !isUploading && handleSlotUpload(slot.index)}
                          style={{
                            border: `2px dashed ${C.gray200}`, borderRadius: 10, padding: 40, textAlign: "center",
                            cursor: isUploading ? "not-allowed" : "pointer", transition: "border-color 0.2s",
                          }}
                          onMouseEnter={e => (e.currentTarget.style.borderColor = C.blue)}
                          onMouseLeave={e => (e.currentTarget.style.borderColor = C.gray200)}
                        >
                          <Upload size={28} color={C.gray300} style={{ marginBottom: 8 }} />
                          <p style={{ fontSize: 14, fontWeight: 500, color: C.gray500, marginBottom: 4 }}>
                            {isUploading ? 'Uploading...' : 'Click to upload image'}
                          </p>
                          <p style={{ fontSize: 12, color: C.gray400 }}>JPG, PNG, or WebP — max 10MB</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Card Text Editing */}
            {!imagesLoading && (
              <div style={{ marginTop: 32 }}>
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em", marginBottom: 4 }}>Card Text</h3>
                  <p style={{ fontSize: 14, color: C.gray400 }}>Edit the text shown on the hero cards. Leave fields blank to use defaults.</p>
                </div>
                {[
                  { label: 'Front Card', index: 0 },
                  { label: 'Back Card', index: 1 },
                ].map(slot => {
                  const card = heroCards[slot.index];
                  const updateField = (field: keyof HeroCardText, value: string) => {
                    setHeroCards(prev => {
                      const updated = [...prev];
                      updated[slot.index] = { ...updated[slot.index], [field]: value };
                      return updated;
                    });
                  };
                  const inputStyle: React.CSSProperties = {
                    width: "100%", padding: "10px 14px", border: `1.5px solid ${C.gray200}`,
                    borderRadius: 8, fontSize: 14, fontFamily: "inherit", color: C.black,
                    outline: "none", transition: "border-color 0.2s",
                  };
                  const labelStyle: React.CSSProperties = {
                    fontSize: 12, fontWeight: 500, color: C.gray500, marginBottom: 4, display: "block",
                  };
                  return (
                    <div key={slot.index} style={{ ...cardStyle, marginBottom: 16 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{slot.label}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Robot Name</label>
                          <input style={inputStyle} value={card.name} onChange={e => updateField('name', e.target.value)}
                            placeholder="e.g. Atlas Pro"
                            onFocus={e => (e.target.style.borderColor = C.black)} onBlur={e => (e.target.style.borderColor = C.gray200)} />
                        </div>
                        <div>
                          <label style={labelStyle}>Category</label>
                          <input style={inputStyle} value={card.category} onChange={e => updateField('category', e.target.value)}
                            placeholder="e.g. Humanoid"
                            onFocus={e => (e.target.style.borderColor = C.black)} onBlur={e => (e.target.style.borderColor = C.gray200)} />
                        </div>
                        <div>
                          <label style={labelStyle}>Price</label>
                          <input style={inputStyle} value={card.price} onChange={e => updateField('price', e.target.value)}
                            placeholder="e.g. $85"
                            onFocus={e => (e.target.style.borderColor = C.black)} onBlur={e => (e.target.style.borderColor = C.gray200)} />
                        </div>
                        <div>
                          <label style={labelStyle}>Location</label>
                          <input style={inputStyle} value={card.location} onChange={e => updateField('location', e.target.value)}
                            placeholder="e.g. San Francisco, CA"
                            onFocus={e => (e.target.style.borderColor = C.black)} onBlur={e => (e.target.style.borderColor = C.gray200)} />
                        </div>
                        {slot.index === 0 && (
                          <div>
                            <label style={labelStyle}>Owner</label>
                            <input style={inputStyle} value={card.owner} onChange={e => updateField('owner', e.target.value)}
                              placeholder="e.g. Owner: Alex M."
                              onFocus={e => (e.target.style.borderColor = C.black)} onBlur={e => (e.target.style.borderColor = C.gray200)} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <button
                  onClick={async () => {
                    setCardTextSaving(true);
                    try {
                      await adminService.updateHomepageContent(heroImages, heroCards);
                      toast.success('Card text saved');
                    } catch { toast.error('Failed to save card text'); }
                    finally { setCardTextSaving(false); }
                  }}
                  disabled={cardTextSaving}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "10px 24px", borderRadius: 100, fontSize: 14, fontWeight: 500,
                    background: C.blue, color: C.pureWhite, border: "none",
                    cursor: cardTextSaving ? "not-allowed" : "pointer", fontFamily: "inherit",
                    opacity: cardTextSaving ? 0.7 : 1, transition: "all 0.2s",
                  }}
                >
                  {cardTextSaving ? 'Saving...' : 'Save Card Text'}
                </button>
              </div>
            )}
          </div>
        );
      }

      case 'users':
        return (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em", marginBottom: 4 }}>Users</h3>
              <p style={{ fontSize: 14, color: C.gray400 }}>View registered users and manage admin access.</p>
            </div>
            {usersLoading ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <div style={{ width: 32, height: 32, border: `3px solid ${C.gray200}`, borderTopColor: C.blue, borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
              </div>
            ) : users.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <Users size={36} color={C.gray300} />
                <p style={{ fontSize: 14, color: C.gray400, marginTop: 16 }}>No users found.</p>
              </div>
            ) : (
              <div style={cardStyle}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>User</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Joined</th>
                      <th style={thStyle}>Location</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id}>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            {user.avatar ? (
                              <img src={user.avatar} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                            ) : (
                              <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.gray50, border: `1px solid ${C.gray100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Users size={14} color={C.gray400} />
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 500 }}>{user.firstName} {user.lastName}</div>
                              <div style={{ fontSize: 12, color: C.gray400 }}>@{user.username}</div>
                            </div>
                          </div>
                        </td>
                        <td style={tdStyle}>{user.email}</td>
                        <td style={tdStyle}>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td style={tdStyle}>{user.location || '—'}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {user.id === currentUser?.uid ? (
                            <span style={{ fontSize: 12, fontWeight: 500, color: C.blue, padding: "3px 10px", borderRadius: 100, border: `1px solid ${C.blue}` }}>Admin (you)</span>
                          ) : (
                            <button
                              onClick={() => handleRoleChange(user.id, user.role)}
                              style={{
                                fontSize: 12, fontWeight: 500, padding: "3px 10px", borderRadius: 100, cursor: "pointer",
                                background: "transparent", fontFamily: "inherit",
                                color: user.role === 'admin' ? C.blue : C.gray400,
                                border: `1px solid ${user.role === 'admin' ? C.blue : C.gray200}`,
                                transition: "all 0.2s",
                              }}
                            >
                              {user.role === 'admin' ? 'Admin' : 'User'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'reports': {
        const statusColors: Record<string, string> = { pending: '#f59e0b', reviewed: C.blue, resolved: '#22c55e', dismissed: C.gray400 };
        const statusIcons: Record<string, React.ReactNode> = {
          pending: <AlertCircle size={14} />, reviewed: <Eye size={14} />,
          resolved: <CheckCircle size={14} />, dismissed: <XCircle size={14} />,
        };
        const filtered = reportFilter === 'all' ? reports : reports.filter(r => r.status === reportFilter);
        const reasonLabels: Record<string, string> = {
          inappropriate_content: 'Inappropriate content', scam_fraud: 'Scam / fraud',
          offensive_behavior: 'Offensive behavior', spam: 'Spam', other: 'Other',
        };
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em", marginBottom: 4 }}>Reports</h3>
                <p style={{ fontSize: 14, color: C.gray400 }}>Review flagged content from users.</p>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {['all', 'pending', 'reviewed', 'resolved', 'dismissed'].map(f => (
                  <button key={f} onClick={() => setReportFilter(f)} style={{
                    padding: "6px 14px", borderRadius: 100, fontSize: 12, fontWeight: 500,
                    background: reportFilter === f ? C.black : "transparent",
                    color: reportFilter === f ? C.pureWhite : C.gray500,
                    border: `1px solid ${reportFilter === f ? C.black : C.gray200}`,
                    cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
                  }}>{f}</button>
                ))}
              </div>
            </div>

            {reportsLoading ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <div style={{ width: 32, height: 32, border: `3px solid ${C.gray200}`, borderTopColor: C.blue, borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <Flag size={36} color={C.gray300} />
                <p style={{ fontSize: 14, color: C.gray400, marginTop: 16 }}>
                  {reportFilter === 'all' ? 'No reports yet.' : `No ${reportFilter} reports.`}
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {filtered.map(report => (
                  <div key={report.id} style={{ ...cardStyle, display: "flex", gap: 20 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                          color: statusColors[report.status], padding: "2px 10px",
                          borderRadius: 100, border: `1px solid ${statusColors[report.status]}20`,
                          background: `${statusColors[report.status]}10`,
                        }}>
                          {statusIcons[report.status]} {report.status}
                        </span>
                        <span style={{ fontSize: 12, color: C.gray400, textTransform: "capitalize" }}>{report.targetType}</span>
                        <span style={{ fontSize: 12, color: C.gray300 }}>•</span>
                        <span style={{ fontSize: 12, color: C.gray400 }}>{reasonLabels[report.reason] || report.reason}</span>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                        Reported: <span style={{ color: C.blue }}>{report.targetName}</span>
                      </div>
                      <p style={{ fontSize: 13, color: C.gray500, marginBottom: 8, lineHeight: 1.5 }}>{report.description}</p>
                      <div style={{ fontSize: 12, color: C.gray400 }}>
                        By {report.reporterName} • {new Date(report.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </div>
                    </div>
                    {report.status === 'pending' && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                        <button onClick={() => handleReportAction(report.id, 'reviewed')} style={{
                          display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 100,
                          fontSize: 12, fontWeight: 500, background: "transparent", color: C.blue,
                          border: `1px solid ${C.blue}`, cursor: "pointer", fontFamily: "inherit",
                        }}><Eye size={13} /> Review</button>
                        <button onClick={() => handleReportAction(report.id, 'resolved')} style={{
                          display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 100,
                          fontSize: 12, fontWeight: 500, background: "transparent", color: "#22c55e",
                          border: `1px solid #22c55e`, cursor: "pointer", fontFamily: "inherit",
                        }}><CheckCircle size={13} /> Resolve</button>
                        <button onClick={() => handleReportAction(report.id, 'dismissed')} style={{
                          display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 100,
                          fontSize: 12, fontWeight: 500, background: "transparent", color: C.gray400,
                          border: `1px solid ${C.gray200}`, cursor: "pointer", fontFamily: "inherit",
                        }}><XCircle size={13} /> Dismiss</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'analytics': {
        const maxDayCount = analyticsData?.viewsByDay.reduce((m, d) => Math.max(m, d.count), 0) || 1;
        const pageLabels: Record<string, string> = {
          '/': 'Home', '/robots': 'Browse Robots', '/about': 'About',
          '/login': 'Login', '/register': 'Register', '/dashboard': 'Dashboard',
          '/messages': 'Messages', '/create-robot': 'Create Robot', '/admin': 'Admin',
          '/profile': 'Profile',
        };
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em", marginBottom: 4 }}>Analytics</h3>
                <p style={{ fontSize: 14, color: C.gray400 }}>Page views across the site.</p>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[7, 14, 30].map(d => (
                  <button key={d} onClick={() => { setAnalyticsDays(d); fetchAnalytics(d); }} style={{
                    padding: "6px 14px", borderRadius: 100, fontSize: 12, fontWeight: 500,
                    background: analyticsDays === d ? C.black : "transparent",
                    color: analyticsDays === d ? C.pureWhite : C.gray500,
                    border: `1px solid ${analyticsDays === d ? C.black : C.gray200}`,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>{d}d</button>
                ))}
              </div>
            </div>

            {analyticsLoading ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <div style={{ width: 32, height: 32, border: `3px solid ${C.gray200}`, borderTopColor: C.blue, borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
              </div>
            ) : !analyticsData || analyticsData.totalViews === 0 ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: 48 }}>
                <BarChart3 size={36} color={C.gray300} />
                <p style={{ fontSize: 14, color: C.gray400, marginTop: 16 }}>No analytics data yet. Views will appear as visitors browse the site.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Total views card */}
                <div style={{ ...cardStyle, display: "flex", gap: 32 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Total Page Views</div>
                    <div style={{ fontSize: 36, fontWeight: 700 }}>{analyticsData.totalViews.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Avg / Day</div>
                    <div style={{ fontSize: 36, fontWeight: 700 }}>{analyticsData.viewsByDay.length > 0 ? Math.round(analyticsData.totalViews / analyticsData.viewsByDay.length) : 0}</div>
                  </div>
                </div>

                {/* Bar chart */}
                <div style={cardStyle}>
                  <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 16 }}>Views by Day</div>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 160 }}>
                    {analyticsData.viewsByDay.map(d => (
                      <div key={d.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <div style={{ fontSize: 10, fontWeight: 500, color: C.gray400 }}>{d.count}</div>
                        <div style={{
                          width: "100%", minHeight: 4, borderRadius: 4,
                          height: `${(d.count / maxDayCount) * 130}px`,
                          background: C.blue, transition: "height 0.3s",
                        }} />
                        <div style={{ fontSize: 9, color: C.gray400, whiteSpace: "nowrap", transform: "rotate(-45deg)", transformOrigin: "top left", marginTop: 4 }}>
                          {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top pages */}
                <div style={cardStyle}>
                  <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 16 }}>Top Pages</div>
                  {analyticsData.viewsByPage.slice(0, 10).map((p, i) => {
                    const maxPageCount = analyticsData.viewsByPage[0]?.count || 1;
                    const label = pageLabels[p.page] || p.page;
                    return (
                      <div key={p.page} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < 9 ? `1px solid ${C.gray100}` : "none" }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: C.gray400, minWidth: 20, textAlign: "right" }}>{i + 1}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
                            <span style={{ fontSize: 13, fontWeight: 500, color: C.gray500 }}>{p.count.toLocaleString()}</span>
                          </div>
                          <div style={{ height: 4, borderRadius: 2, background: C.gray100 }}>
                            <div style={{ height: "100%", borderRadius: 2, background: C.blue, width: `${(p.count / maxPageCount) * 100}%`, transition: "width 0.3s" }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <div style={{ fontFamily: "'Satoshi', sans-serif", color: C.black }}>
      <Navbar />

      {/* Header */}
      <section className="m-section-page-hero" style={{ background: C.black, color: C.pureWhite, padding: "120px 48px 48px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: C.gray400, marginBottom: 16 }}>Admin Console</div>
          <h1 style={{ fontSize: "clamp(30px, 3.5vw, 44px)", fontWeight: 400, letterSpacing: "-0.025em" }}>Site Management</h1>
        </div>
      </section>

      <section className="m-section" style={{ background: C.gray50, padding: "48px 48px 100px" }}>
        <div className="m-sidebar-layout" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "220px 1fr", gap: 32 }}>
          {/* Sidebar */}
          <div>
            <div style={cardStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.gray100}` }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.blue, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Settings size={16} color={C.pureWhite} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Admin</div>
                  <div style={{ fontSize: 12, color: C.gray400 }}>{currentUser?.email}</div>
                </div>
              </div>
              <nav style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {tabs.map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 14px", borderRadius: 8, fontSize: 14, fontWeight: 500,
                      background: activeTab === tab.id ? C.gray50 : "transparent",
                      color: activeTab === tab.id ? C.black : C.gray500,
                      border: "none", cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.15s", textAlign: "left", width: "100%",
                    }}>
                    {tab.icon} {tab.name}
                    {tab.badge ? (
                      <span style={{
                        marginLeft: "auto", fontSize: 11, fontWeight: 600,
                        background: "#ef4444", color: C.pureWhite,
                        borderRadius: 100, padding: "1px 7px", minWidth: 18, textAlign: "center",
                      }}>{tab.badge}</span>
                    ) : null}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div>{renderContent()}</div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminPage;
